"use client";

import Link from "next/link";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { Search } from "lucide-react";
import { Container } from "@/components/ui/Container";
import { SearchModal } from "@/components/search/SearchModal";
import { LinkButton } from "@/components/ui/Button";
import { LangToggle } from "./LangToggle";
import { NavMobileToggle } from "./NavMobileToggle";
import { AccountMenu } from "./AccountMenu";
import { AssistantMark } from "./AssistantMark";
import { cn } from "@/components/ui/cn";
import { langPath, type Lang } from "@/lib/i18n";
import type { NavLayout, NavLink } from "@/lib/pages/site-schema";

type Props = {
  lang: Lang;
  isAuthed: boolean;
  userName: string;
  links: NavLink[];
  /** Desktop arrangement of logo + links (see NAV_LAYOUTS). Absent → "inline". */
  layout?: NavLayout;
  /** Pin the header on scroll on interior (non-overlay) pages. Absent → true. */
  sticky?: boolean;
  /** Enabled portal locales for the language switcher (O8). */
  languages: string[];
  /** Portal display name — the logo's alt/aria text, and the wordmark shown
   *  when the portal has no logo image. */
  brandName: string;
  /** Brand marks by surface; blank → wordmark. Never a bundled asset. */
  logos: { light: string; dark: string };
  /** Authored utility-bar link. Absent → no utility bar. */
  promo?: { href: string; label: string; external: boolean } | null;
  /** Assistant-as-search entry (gated on the portal's ai_chat toggle server-side
   *  — see Nav). The assistant IS the site's search: it answers "find me X"
   *  with catalog rows as citations. Null → no entry. */
  ask?: { href: string; label: string } | null;
  primaryNavLabel: string;
  /** `portal.registration.enabled` — false hides every "create account" entry. */
  registrationOpen: boolean;
  labels: {
    login: string;
    register: string;
    profile: string;
    logout: string;
    openMenu: string;
    closeMenu: string;
  };
};

export function MarketingHeader({
  lang,
  isAuthed,
  userName,
  links,
  layout = "inline",
  sticky = true,
  languages,
  brandName,
  logos,
  promo = null,
  ask = null,
  primaryNavLabel,
  registrationOpen,
  labels,
}: Props) {
  // The header lives in a persistent layout — it does not re-render on
  // client navigation. usePathname() does, so all route-derived state
  // (overlay mode, active link, login redirect) is read from it here.
  const pathname = usePathname() ?? `/${lang}`;
  const [atHeroTop, setAtHeroTop] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // Overlay mode is declared by the CONTENT, not a path list: every block that
  // renders a dark full-bleed band at the top of a page (Hero's dark layouts,
  // AcademyHero, PageHeader dark, DetailHeroBanner) marks its section with
  // [data-header-overlay]. A path list can't know what an authored document
  // contains — when the homepage's Hero block was swapped for light content,
  // the hardcoded "overlay" entry rendered a white-on-white (invisible) header.
  // False is both the server-rendered value and the safe state (frosted, dark
  // text — legible on anything), so a page only goes transparent after JS
  // confirms a dark band actually sits under the header.
  const [overlayHero, setOverlayHero] = useState(false);
  const headerRef = useRef<HTMLElement>(null);

  // The header's RENDERED height as `--marketing-header-h`, on the root: the
  // stylesheet's default (6.75rem) assumes the utility bar, which only renders
  // when the portal authored a utility link — without it the header is ~80px
  // and everything pinned to the variable (a sticky reader strip, a sticky
  // sidebar, the floating right unit) would hang 28px below the header with
  // the page showing through. Zero when the header is in flow (`relative`):
  // nothing is over the page then, so nothing needs clearing. The CSS default
  // stands on the server and until this runs; the root's inline value wins
  // after, and follows resizes.
  useEffect(() => {
    const el = headerRef.current;
    const root = document.documentElement;
    if (!el) return;
    const publish = () => {
      const pos = getComputedStyle(el).position;
      const h = pos === "fixed" || pos === "sticky" ? el.offsetHeight : 0;
      root.style.setProperty("--marketing-header-h", `${h}px`);
    };
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.removeProperty("--marketing-header-h");
    };
  }, [overlayHero, sticky]);
  useEffect(() => {
    const update = () => {
      const el = document.querySelector("[data-header-overlay]");
      // Top-of-page only: the same blocks can legitimately appear mid-page,
      // where they say nothing about what's under the header.
      setOverlayHero(el ? el.getBoundingClientRect().top + window.scrollY < 300 : false);
    };
    const raf = requestAnimationFrame(update);
    // The route commit can precede the page's sections: loading.tsx streams a
    // skeleton first, the real content (and its marker) arrives later within
    // the same pathname. Watch the DOM so the late marker still flips the mode.
    const mo = new MutationObserver(update);
    mo.observe(document.body, { childList: true, subtree: true });
    return () => {
      cancelAnimationFrame(raf);
      mo.disconnect();
    };
  }, [pathname]);

  const loginHref = `${langPath(lang, "/login")}?redirect=${encodeURIComponent(pathname)}`;

  // Transparent over the dark hero photo, frosted-glass once content scrolls
  // under it — but only as a progressive enhancement. `atHeroTop` is false on
  // the server and stays false until JS confirms we're at the top of an overlay
  // hero, so the header renders FROSTED (dark-on-light, legible) by default. A
  // transparent/white header stranded over light content would be unreadable,
  // so the safe state — not the pretty one — is the default that survives no-JS
  // or a failed hydration. (This is also why the previous scroll-listener build
  // looked broken when hydration didn't run: its default was the unsafe state.)
  //
  // Driven by IntersectionObserver, not a scroll listener: the callback fires
  // only when crossing the 24px threshold (twice per scroll-through), never per
  // frame, so it does no main-thread work during touch scrolling — where a
  // scroll handler janks most. The sentinel is a 24px strip pinned to the top of
  // the document, so "intersecting" means "within 24px of the top" == over the
  // hero.
  // Non-overlay pages are always frosted (see `onFrost`), so `atHeroTop` is
  // irrelevant there — skip the observer entirely. A stale `true` carried over
  // from a previous overlay page is harmless because `onFrost` ignores it.
  useEffect(() => {
    if (!overlayHero) return;
    const sentinel = document.createElement("div");
    sentinel.setAttribute("aria-hidden", "true");
    sentinel.style.cssText =
      "position:absolute;top:0;left:0;width:1px;height:24px;pointer-events:none";
    document.body.appendChild(sentinel);
    const io = new IntersectionObserver((entries) =>
      setAtHeroTop(entries[0]?.isIntersecting ?? false),
    );
    io.observe(sentinel);
    return () => {
      io.disconnect();
      sentinel.remove();
    };
  }, [overlayHero]);

  // Two content tones: light-on-dark (transparent over the hero) and
  // dark-on-frosted (non-overlay pages, or anytime JS hasn't confirmed we're at
  // the hero top). Frosted is the legible default.
  const onFrost = !overlayHero || !atHeroTop;

  return (
    <header
      ref={headerRef}
      role="banner"
      className={cn(
        "inset-x-0 top-0 z-30 transition-colors duration-300",
        // Hero/listing pages stay `fixed` so the header overlays the dark hero;
        // interior pages honour the author's sticky toggle. Default sticky:true
        // keeps this exactly "fixed"/"sticky" as before.
        overlayHero ? "fixed" : sticky ? "sticky" : "relative",
        onFrost
          ? // Editorial masthead shadow: a hairline for the edge plus a long, faint
            // drop that lifts the bar off the page without reading as a card.
            "bg-white/85 text-foreground backdrop-blur-md border-b border-border/50 shadow-[0_1px_2px_rgb(0_0_0/0.04),0_12px_32px_-20px_rgb(0_0_0/0.28)]"
          : "bg-transparent text-white",
      )}
    >
      {/* Utility bar — one AUTHORED promo link. Absent by default: this used to
          be a hardcoded link to fork #0's own price page, so every portal built
          from this template advertised another company in its header. */}
      {promo ? (
        <div
          className={cn(
            "transition-colors duration-300",
            onFrost ? "bg-off-white/60 border-b border-border/40" : "bg-black/15",
          )}
        >
          <Container className="flex items-center justify-center py-1.5 text-xs">
            <a
              href={promo.href}
              {...(promo.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
              className={cn(
                "font-semibold transition-colors hover:text-gold",
                onFrost ? "text-mid-gray" : "text-white/80",
              )}
            >
              {promo.label} →
            </a>
          </Container>
        </div>
      ) : null}

      {/* Main nav row. The three groups (logo · links · actions) are extracted so
          the layout can rearrange them without duplicating markup. `inline` is
          the original arrangement, kept byte-identical — every cn() conditional
          collapses to the original class string when layout === "inline". */}
      {(() => {
        // The mark is the PORTAL's, resolved server-side (authored identity →
        // portal.logo → nothing). No bundled fallback: a hardcoded asset here
        // shipped fork #0's logo to every portal built from this template.
        // Without an image the brand NAME renders as a wordmark, which is always
        // correct even if it is plainer.
        const mark = onFrost ? logos.light : logos.dark || logos.light;
        const logo = (
          <Link
            href={langPath(lang, "/")}
            aria-label={brandName}
            className="inline-flex items-center"
          >
            {mark ? (
              <Image
                src={mark}
                alt={brandName}
                // Sized by height only (`h-8 w-auto`), so width/height are just
                // an aspect hint — any portal's lockup scales to the same bar.
                width={443}
                height={85}
                loading="eager"
                className="h-8 w-auto"
              />
            ) : (
              <span
                className={cn(
                  "font-display text-lg font-extrabold tracking-tight",
                  onFrost ? "text-foreground" : "text-white",
                )}
              >
                {brandName}
              </span>
            )}
          </Link>
        );

        const linkEl = (l: NavLink) => {
          const active = pathname === l.href || pathname.startsWith(l.href + "/");
          return (
            <Link
              key={l.href}
              href={l.href}
              aria-current={active ? "page" : undefined}
              className={cn(
                "py-3 tracking-[0.005em] transition-colors",
                // The assistant's entry carries her mark before the name.
                l.assistant && "inline-flex items-center gap-1.5",
                active
                  ? "text-gold border-b-2 border-gold"
                  : onFrost
                    ? "text-foreground hover:text-gold"
                    : "text-white hover:text-gold",
              )}
            >
              {l.assistant ? <AssistantMark /> : null}
              {l.label}
            </Link>
          );
        };

        const navEl = (
          <nav
            className={cn(
              // gap-9 (36px): nav links need more air than a toolbar — the
              // previous 24px read as a run-on word list next to the logo.
              "hidden md:flex items-center gap-9",
              // Grow into the space between logo and actions and centre within
              // it — reserves room, so many links never overlap (unlike absolute
              // centring).
              layout === "centered" && "flex-1 justify-center",
            )}
            aria-label={primaryNavLabel}
          >
            {links.map(linkEl)}
          </nav>
        );

        // logoCenter splits the links around the logo: first half hugs it from
        // the left, the rest (plus the coded actions) fill the right zone. The
        // equal flex-1 zones are what keep the logo optically centred.
        const mid = Math.ceil(links.length / 2);

        const actions = (
          <div className="hidden md:flex items-center gap-4">
            {ask ? (
              // Icon-only on purpose: the full "find or ask" sentence next to the
              // lang chips + login + register overflowed the row whenever a portal
              // had a real nav (7 links × 5 locales broke it). The label stays as
              // tooltip + sr-only text; AppTopBar uses the same compact pattern.
              // Click opens the SearchModal palette; the href is the no-JS
              // fallback (and what prefetch/open-in-new-tab get).
              <Link
                href={ask.href}
                title={ask.label}
                onClick={(e) => {
                  e.preventDefault();
                  setSearchOpen(true);
                }}
                className={cn(
                  "inline-flex size-9 shrink-0 items-center justify-center rounded-full border transition-colors",
                  onFrost
                    ? "border-border/70 text-mid-gray hover:border-foreground/30 hover:text-foreground"
                    : "border-white/30 text-white/85 hover:border-white/60 hover:text-white",
                )}
              >
                <Search className="size-4" aria-hidden />
                <span className="sr-only">{ask.label}</span>
              </Link>
            ) : null}
            <LangToggle current={lang} tone={onFrost ? "dark" : "light"} languages={languages} />
            {isAuthed ? (
              <AccountMenu lang={lang} name={userName} />
            ) : (
              <>
                <Link
                  href={loginHref}
                  className={cn(
                    "py-2 transition-colors hover:text-gold",
                    onFrost ? "text-foreground" : "text-white",
                  )}
                >
                  {labels.login}
                </Link>
                {registrationOpen ? (
                  <LinkButton href={langPath(lang, "/register")} variant="primary" size="compact">
                    {labels.register}
                  </LinkButton>
                ) : null}
              </>
            )}
          </div>
        );

        const mobile = (
          <NavMobileToggle
            lang={lang}
            isAuthed={isAuthed}
            accountLabel={userName}
            loginHref={loginHref}
            links={links}
            languages={languages}
            tone={onFrost ? "dark" : "light"}
            labels={{
              login: labels.login,
              register: labels.register,
              open: labels.openMenu,
              close: labels.closeMenu,
              profile: labels.profile,
              logout: labels.logout,
            }}
          />
        );

        return (
          <Container className="flex items-center justify-between gap-10 py-4">
            {layout === "logoCenter" ? (
              <>
                <nav
                  className="hidden md:flex flex-1 items-center justify-end gap-9"
                  aria-label={primaryNavLabel}
                >
                  {links.slice(0, mid).map(linkEl)}
                </nav>
                {logo}
                <div className="hidden md:flex flex-1 items-center justify-between gap-4">
                  <div className="flex items-center gap-9">{links.slice(mid).map(linkEl)}</div>
                  {actions}
                </div>
              </>
            ) : layout === "left" ? (
              <div className="flex items-center gap-12">
                {logo}
                {navEl}
              </div>
            ) : (
              <>
                {logo}
                {navEl}
              </>
            )}
            {layout !== "logoCenter" ? actions : null}
            <div className="flex items-center md:hidden">
              {/* The ask/search entry was desktop-only; on mobile it rides next
                  to the hamburger (the drawer stays nav+account only). */}
              {ask ? (
                <Link
                  href={ask.href}
                  onClick={(e) => {
                    e.preventDefault();
                    setSearchOpen(true);
                  }}
                  className={cn(
                    "inline-flex size-12 items-center justify-center transition-colors hover:text-gold",
                    onFrost ? "text-teal" : "text-white",
                  )}
                >
                  <Search className="size-5" aria-hidden />
                  <span className="sr-only">{ask.label}</span>
                </Link>
              ) : null}
              {mobile}
            </div>
          </Container>
        );
      })()}
      {ask ? (
        <SearchModal
          lang={lang}
          isAuthed={isAuthed}
          open={searchOpen}
          onOpen={() => setSearchOpen(true)}
          onClose={() => setSearchOpen(false)}
        />
      ) : null}
    </header>
  );
}
